import {
  Box,
  FormControl,
  Text,
  Input,
  Link,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  HStack,
  Image,
} from '@chakra-ui/react'

interface URLCardProps {
  value: string
  onChange: (val: string) => void
  isReadOnly?: boolean
}

const URLCard: React.FC<URLCardProps> = ({ value, onChange, isReadOnly }) => {
  // Normalize URLs without protocol so they still open correctly
  const normalizeUrl = (url: string) => {
    if (!url) return ''
    const trimmed = url.trim()
    if (/^https?:\/\//i.test(trimmed)) return trimmed
    return `https://${trimmed}`
  }

  const normalized = normalizeUrl(value)
  const getHost = (url: string) => {
    try {
      const u = new URL(url)
      return u.hostname
    } catch {
      return ''
    }
  }
  const host = normalized ? getHost(normalized) : ''

  return (
    <Box className="url-card" w="100%">
      <FormControl>
        {isReadOnly ? (
          <Box
            p="16px"
            borderWidth="1px"
            borderColor="solid.50"
            borderRadius="8px"
            bg="solid.0"
          >
            {value ? (
              <Popover trigger="hover" placement="top-start" openDelay={150} closeDelay={50} gutter={8}>
                <PopoverTrigger>
                  <Link
                    href={normalized}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="solid.900"
                    wordBreak="break-all"
                    _hover={{ textDecoration: 'none' }}
                  >
                    <Box>
                      <HStack align="center" spacing={3}>
                        {host ? (
                          <Image
                            src={`https://www.google.com/s2/favicons?domain=${host}&sz=64`}
                            alt={`${host} favicon`}
                            boxSize="28px"
                            borderRadius="6px"
                          />
                        ) : null}
                        <Box>
                          {host ? (
                            <Text textStyle="h-xxs" color="solid.900">{host}</Text>
                          ) : null}
                          <Text textStyle="l-xs" color="solid.700" noOfLines={2}>
                            {value}
                          </Text>
                        </Box>
                      </HStack>
                      <Text textStyle="l-xxs" color="solid.500" mt={3}>
                        Open learning link in a new tab
                      </Text>
                    </Box>
                  </Link>
                </PopoverTrigger>
                <PopoverContent w="auto" maxW="360px" borderColor="solid.50" boxShadow="md" _focus={{ outline: 'none' }}>
                  <PopoverArrow />
                  <PopoverBody>
                    <HStack align="flex-start" spacing={3}>
                      {host ? (
                        <Image
                          src={`https://www.google.com/s2/favicons?domain=${host}&sz=64`}
                          alt={`${host} favicon`}
                          boxSize="24px"
                          borderRadius="4px"
                        />
                      ) : null}
                      <Box>
                        {host ? (
                          <Text textStyle="h-xxs" color="solid.900">{host}</Text>
                        ) : null}
                        <Text textStyle="l-xxs" color="solid.500" noOfLines={2}>
                          {normalized}
                        </Text>
                        <Text textStyle="l-xxs" color="solid.500" mt={1}>
                          Opens in a new tab
                        </Text>
                      </Box>
                    </HStack>
                  </PopoverBody>
                </PopoverContent>
              </Popover>
            ) : (
              <Text color="solid.300">No URL provided</Text>
            )}
          </Box>
        ) : (
          <Input
            type="text"
            size="sm"
            name="url"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            isReadOnly={false}
            cursor={'text'}
            bg={'white'}
          />
        )}
      </FormControl>
    </Box>
  )
}

export default URLCard
